'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { ShopifyCart } from '@/lib/shopify/types/cart';
import {
  createCart,
  addToCart,
  updateCartLines,
  removeCartLines,
  fetchCart,
} from '@/lib/shopify/cart';

const CART_STORAGE_KEY = 'impacta_cart_id';

interface CartContextValue {
  cart: ShopifyCart | null;
  itemCount: number;
  loading: boolean;
  isDrawerOpen: boolean;
  addItem: (
    variantId: string,
    quantity?: number,
    attributes?: Array<{ key: string; value: string }>
  ) => Promise<ShopifyCart>;
  removeItem: (lineId: string) => Promise<void>;
  updateQuantity: (lineId: string, quantity: number) => Promise<void>;
  openDrawer: () => void;
  closeDrawer: () => void;
  checkoutUrl: string | null;
}

export const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<ShopifyCart | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Restore cart from localStorage on mount
  useEffect(() => {
    const cartId = localStorage.getItem(CART_STORAGE_KEY);
    if (!cartId) return;

    fetchCart(cartId).then((fetched) => {
      if (fetched) {
        setCart(fetched);
      } else {
        // Expired or invalid cart
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    });
  }, []);

  const addItem = useCallback(
    async (variantId: string, quantity = 1, attributes?: Array<{ key: string; value: string }>) => {
      setLoading(true);
      try {
        const cartId = localStorage.getItem(CART_STORAGE_KEY);

        if (cartId) {
          try {
            const updated = await addToCart(cartId, variantId, quantity, attributes);
            setCart(updated);
            return updated;
          } catch {
            // Cart may be expired -- clear and create new
            localStorage.removeItem(CART_STORAGE_KEY);
          }
        }

        // No existing cart or expired -- create new
        const newCart = await createCart(variantId, quantity, attributes);
        localStorage.setItem(CART_STORAGE_KEY, newCart.id);
        setCart(newCart);
        return newCart;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const removeItem = useCallback(
    async (lineId: string) => {
      const cartId = cart?.id;
      if (!cartId) return;

      setLoading(true);
      try {
        const updated = await removeCartLines(cartId, [lineId]);
        setCart(updated);
        if (updated.totalQuantity === 0) {
          setIsDrawerOpen(false);
        }
      } finally {
        setLoading(false);
      }
    },
    [cart?.id]
  );

  const updateQuantity = useCallback(
    async (lineId: string, quantity: number) => {
      if (quantity <= 0) {
        await removeItem(lineId);
        return;
      }

      const cartId = cart?.id;
      if (!cartId) return;

      setLoading(true);
      try {
        const updated = await updateCartLines(cartId, lineId, quantity);
        setCart(updated);
      } finally {
        setLoading(false);
      }
    },
    [cart?.id, removeItem]
  );

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      itemCount: cart?.totalQuantity ?? 0,
      loading,
      isDrawerOpen,
      addItem,
      removeItem,
      updateQuantity,
      openDrawer,
      closeDrawer,
      checkoutUrl: cart?.checkoutUrl ?? null,
    }),
    [cart, loading, isDrawerOpen, addItem, removeItem, updateQuantity, openDrawer, closeDrawer]
  );

  return React.createElement(CartContext.Provider, { value }, children);
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
